import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, History, Loader2, ListOrdered, RefreshCw } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import type { DashboardProject } from '../../../project/types/project';
import { PIPEFITTER_TAB_TRIGGER_CLASS } from '../../constants/uiConstants';
import {
  extractSubmitErrorMessage,
  getStoredCompatibilityReport,
  listCompatibilityReports,
} from '../../api/compatibilityReportsApi';
import type {
  CompatReportSummary,
  CompatStoredReportDetail,
} from './types';
import { formatPct, formatSeamSeparationMm, formatFitDataUpdatedAt } from './helpers/formatReport';
import { OverviewView } from './views/OverviewView';
import { PerPipeView } from './views/PerPipeView';
import { HandlingView } from './views/HandlingView';
import { TurningView } from './views/TurningView';
import { ShortlistView } from './views/ShortlistView';
import { SinglePassView } from './views/SinglePassView';
import {
  EMPTY_ARRIVAL_ORDER_STATE,
  type ArrivalOrderState,
} from './views/ArrivalOrderPanel';
import { GenerateStakeholderReportButton } from './report/GenerateStakeholderReportButton';
import { GenerateSequenceFromCompatModal } from './GenerateSequenceFromCompatModal';
import type { SetupPipeDefaults } from './views/SetupView';
import { cn } from '../../../../lib/utils';

interface RandomPipeCompatHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: DashboardProject;
  selectedPipe?: SetupPipeDefaults | null;
}

type DetailTab = 'overview' | 'pipes' | 'handling' | 'flipping' | 'shortlist' | 'singlepass' | 'request';

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortId(id: string): string {
  if (!id) return '—';
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

export const RandomPipeCompatHistoryModal: React.FC<RandomPipeCompatHistoryModalProps> = ({
  open,
  onOpenChange,
  project,
  selectedPipe,
}) => {
  const projectNumber = project?.project?.projectNumber ?? '';
  const projectName = project?.project?.projectName ?? '';

  const [reports, setReports] = useState<CompatReportSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CompatStoredReportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>('overview');
  const [arrivalOrder, setArrivalOrder] = useState<ArrivalOrderState>(EMPTY_ARRIVAL_ORDER_STATE);
  const [generateSequenceOpen, setGenerateSequenceOpen] = useState(false);

  const loadList = useCallback(async () => {
    if (!projectNumber) {
      setListError('No project number on this project.');
      setReports([]);
      return;
    }
    setListLoading(true);
    setListError(null);
    try {
      const data = await listCompatibilityReports(projectNumber);
      setReports(data.reports ?? []);
    } catch (err) {
      setReports([]);
      setListError(extractSubmitErrorMessage(err));
    } finally {
      setListLoading(false);
    }
  }, [projectNumber]);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setDetail(null);
      setDetailError(null);
      setTab('overview');
      setArrivalOrder(EMPTY_ARRIVAL_ORDER_STATE);
      setListError(null);
      setGenerateSequenceOpen(false);
      return;
    }
    void loadList();
  }, [open, loadList]);

  useEffect(() => {
    if (!open || !selectedId) {
      setDetail(null);
      setDetailError(null);
      setArrivalOrder(EMPTY_ARRIVAL_ORDER_STATE);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    setTab('overview');
    setArrivalOrder(EMPTY_ARRIVAL_ORDER_STATE);

    void getStoredCompatibilityReport(selectedId)
      .then((row) => {
        if (!cancelled) setDetail(row);
      })
      .catch((err) => {
        if (!cancelled) setDetailError(extractSubmitErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedId]);

  const metaLine = useMemo(() => {
    if (!detail?.result) return null;
    const s = detail.result.settings;
    const seam = formatSeamSeparationMm(s.weldSeamSeparationMm, s.weldSeamExclusionDeg);
    const fitUpdated = formatFitDataUpdatedAt(detail.result.project.fitDataUpdatedAt);
    return (
      <>
        Batch <span className="font-semibold text-text-grey">{detail.result.project.pipeCount} pipes</span>
        {' · '}
        {detail.result.project.reportDate}
        {' · '}
        hi-lo <span className="font-semibold text-text-grey">{s.hiLoThresholdMm} mm</span>
        {' · '}
        seam <span className="font-semibold text-text-grey">{seam}</span>
        {' · '}
        turning{' '}
        <span className="font-semibold text-text-grey">
          {s.flippingAllowed ? 'allowed' : 'off'}
        </span>
        {fitUpdated ? (
          <>
            {' · '}
            fit data updated <span className="font-semibold text-text-grey">{fitUpdated}</span>
          </>
        ) : null}
      </>
    );
  }, [detail]);

  const handleClose = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  const backToList = () => {
    setSelectedId(null);
    setDetail(null);
    setDetailError(null);
  };

  const showingDetail = Boolean(selectedId);

  const showHistoryShortlistTab = Boolean(detail?.result?.shortlist);
  const showHistoryTurningTab = detail?.result?.settings?.flippingAllowed === true;

  useEffect(() => {
    if (tab === 'shortlist' && !showHistoryShortlistTab) setTab('overview');
    if (tab === 'flipping' && !showHistoryTurningTab) setTab('overview');
  }, [tab, showHistoryShortlistTab, showHistoryTurningTab]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="w-screen h-screen max-w-none max-h-[100dvh] m-0 p-0 gap-0 border-0 bg-page-bg shadow-none rounded-none [&>button]:hidden flex flex-col overflow-hidden">
          <DialogHeader className="px-4 sm:px-6 py-4 border-b border-[#E2E5EA] flex-shrink-0 bg-white">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                type="button"
                onClick={() => (showingDetail ? backToList() : handleClose(false))}
                className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
                variant="outline"
                size="icon"
                aria-label={showingDetail ? 'Back to report history list' : 'Close report history'}
              >
                <ArrowLeft className="h-4 w-4 text-text-grey" />
              </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-heritage-grey mb-0.5">
                Random pipe compatibility
              </p>
              <DialogTitle className="font-heading text-[18px] sm:text-[22px] font-bold text-blueprint-blue leading-tight">
                {showingDetail
                  ? `Report ${shortId(selectedId || '')}`
                  : `History · Project ${projectNumber || '—'}`}
              </DialogTitle>
              {showingDetail && metaLine ? (
                <p className="text-[12.5px] text-heritage-grey mt-1 truncate">{metaLine}</p>
              ) : (
                <p className="text-[12.5px] text-heritage-grey mt-1 truncate">
                  {projectName || 'Past submitted reports for this project'}
                </p>
              )}
            </div>
            {!showingDetail && (
              <Button
                type="button"
                variant="outline"
                className="shrink-0 h-9 border-[#E2E5EA]"
                onClick={() => void loadList()}
                disabled={listLoading}
              >
                {listLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span className="ml-2 hidden sm:inline">Refresh</span>
              </Button>
            )}
            {showingDetail && detail?.result && detail.reportId && (
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                <Button
                  type="button"
                  className="h-9 shrink-0 bg-hero-red hover:bg-hero-red/90 text-white"
                  onClick={() => setGenerateSequenceOpen(true)}
                >
                  <ListOrdered className="h-4 w-4" />
                  <span className="ml-2">Generate sequence</span>
                </Button>
                <GenerateStakeholderReportButton
                  reportId={detail.reportId}
                  className="shrink-0"
                />
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="max-w-full mx-auto px-4 sm:px-6 py-5 pb-16">
            {!showingDetail && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white overflow-hidden relative">
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-blueprint-blue" />
                <div className="px-5 pt-5 pb-3 flex items-center gap-2">
                  <History className="h-4 w-4 text-blueprint-blue" />
                  <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey">
                    Submitted reports
                  </p>
                  {!listLoading && !listError && (
                    <span className="text-[12px] text-heritage-grey ml-auto">
                      {reports.length} report{reports.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>

                {listLoading && (
                  <div className="flex items-center justify-center gap-2 py-16 text-heritage-grey text-[13.5px]">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Loading history…
                  </div>
                )}

                {!listLoading && listError && (
                  <div className="mx-5 mb-5 rounded-lg border border-[rgba(235,28,55,0.25)] bg-[rgba(235,28,55,0.06)] p-4 text-[13px] text-hero-red">
                    {listError}
                  </div>
                )}

                {!listLoading && !listError && reports.length === 0 && (
                  <div className="px-5 pb-8 pt-2 text-center text-[13.5px] text-heritage-grey">
                    No saved reports for this project yet. Run a report from Random Pipe
                    Compatibility first.
                  </div>
                )}

                {!listLoading && !listError && reports.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-[13.5px]">
                      <thead>
                        <tr className="bg-page-bg border-y border-[#E2E5EA]">
                          <th className="px-4 py-2.5 text-left text-[10px] font-bold tracking-wide uppercase text-heritage-grey">
                            Created
                          </th>
                          <th className="px-4 py-2.5 text-left text-[10px] font-bold tracking-wide uppercase text-heritage-grey">
                            Report ID
                          </th>
                          <th className="px-4 py-2.5 text-left text-[10px] font-bold tracking-wide uppercase text-heritage-grey">
                            Requested by
                          </th>
                          <th className="px-4 py-2.5 text-right text-[10px] font-bold tracking-wide uppercase text-heritage-grey">
                            Seed
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports.map((row) => (
                          <tr
                            key={row.reportId}
                            className="border-b border-[#EEF0F3] last:border-b-0 hover:bg-[rgba(0,82,147,0.04)] cursor-pointer transition-colors"
                            onClick={() => setSelectedId(row.reportId)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedId(row.reportId);
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Open report ${row.reportId}`}
                          >
                            <td className="px-4 py-3 text-text-grey whitespace-nowrap">
                              {formatWhen(row.createdAt)}
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-semibold text-blueprint-blue font-mono text-[12.5px]">
                                {shortId(row.reportId)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-text-grey">
                              {row.requestedBy || '—'}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-text-grey">
                              {row.baseSeed}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {showingDetail && (
              <>
                {detailLoading && (
                  <div className="flex items-center justify-center gap-2 py-20 text-heritage-grey text-[13.5px]">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Loading report…
                  </div>
                )}

                {!detailLoading && detailError && (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-[rgba(235,28,55,0.25)] bg-[rgba(235,28,55,0.06)] p-4 text-[13px] text-hero-red">
                      {detailError}
                    </div>
                    <Button type="button" variant="outline" onClick={backToList}>
                      Back to list
                    </Button>
                  </div>
                )}

                {!detailLoading && detail?.result && (
                  <Tabs
                    value={tab}
                    onValueChange={(v) => setTab(v as DetailTab)}
                    className="w-full"
                  >
                    <TabsList className="h-auto w-full justify-start gap-4 bg-transparent px-0 py-0 border-b border-[#E2E5EA] rounded-none mb-5 flex-wrap md:gap-6 lg:gap-8">
                      {(
                        [
                          { key: 'overview', label: 'Overview', visible: true },
                          { key: 'pipes', label: 'Per pipe', visible: true },
                          { key: 'handling', label: 'Handling', visible: true },
                          {
                            key: 'flipping',
                            label: 'Turning',
                            visible: showHistoryTurningTab,
                          },
                          {
                            key: 'shortlist',
                            label: 'Shortlist',
                            visible: showHistoryShortlistTab,
                          },
                          { key: 'singlepass', label: 'Single pass', visible: true },
                          { key: 'request', label: 'Request', visible: true },
                        ] as const
                      )
                        .filter((t) => t.visible)
                        .map((t) => (
                        <TabsTrigger
                          key={t.key}
                          value={t.key}
                          className={cn(
                            PIPEFITTER_TAB_TRIGGER_CLASS,
                            'shrink-0 px-2'
                          )}
                        >
                          {t.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    <TabsContent value="overview" className="mt-0">
                      <OverviewView
                        result={detail.result}
                        reportId={detail.reportId}
                        arrivalOrder={arrivalOrder}
                        onArrivalOrderChange={setArrivalOrder}
                      />
                    </TabsContent>
                    <TabsContent value="pipes" className="mt-0">
                      <PerPipeView result={detail.result} />
                    </TabsContent>
                    <TabsContent value="handling" className="mt-0">
                      <HandlingView result={detail.result} />
                    </TabsContent>
                    <TabsContent value="flipping" className="mt-0">
                      <TurningView result={detail.result} />
                    </TabsContent>
                    <TabsContent value="shortlist" className="mt-0">
                      <ShortlistView result={detail.result} />
                    </TabsContent>
                    <TabsContent value="singlepass" className="mt-0">
                      <SinglePassView result={detail.result} />
                    </TabsContent>
                    <TabsContent value="request" className="mt-0">
                      <RequestSummaryPanel
                        detail={detail}
                        listRequestedBy={
                          reports.find((r) => r.reportId === detail.reportId)?.requestedBy
                        }
                      />
                    </TabsContent>
                  </Tabs>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

      {showingDetail && detail && detail.result ? (
        <GenerateSequenceFromCompatModal
          open={generateSequenceOpen}
          onOpenChange={setGenerateSequenceOpen}
          project={project}
          result={detail.result}
          selectedPipe={selectedPipe}
        />
      ) : null}
    </>
  );
};

function RequestSummaryPanel({
  detail,
  listRequestedBy,
}: {
  detail: CompatStoredReportDetail;
  /** From the history list row when the detail payload omits it (job poll-done shape). */
  listRequestedBy?: string | null;
}) {
  const req = detail.request || {};
  const settings = (detail.settings || {}) as Record<string, unknown>;
  const result = detail.result;
  const run = result?.run;
  const resultSettings = result?.settings;

  const pipeIds = Array.isArray(req.pipeIds) ? (req.pipeIds as string[]) : null;

  const confidence =
    optionalNumber(req.confidence) ??
    optionalNumber(run?.confidence);
  const margin =
    optionalNumber(req.margin) ??
    optionalNumber(run?.targetMargin);
  const hiLo =
    optionalNumber(req.hiLoThreshold) ??
    optionalNumber(settings.hiLoThresholdMm) ??
    optionalNumber(resultSettings?.hiLoThresholdMm);
  const flipping =
    typeof req.flippingAllowed === 'boolean'
      ? req.flippingAllowed
      : typeof settings.flippingAllowed === 'boolean'
        ? (settings.flippingAllowed as boolean)
        : typeof resultSettings?.flippingAllowed === 'boolean'
          ? resultSettings.flippingAllowed
          : null;
  const requestedBy =
    (typeof detail.requestedBy === 'string' && detail.requestedBy) ||
    (typeof listRequestedBy === 'string' && listRequestedBy) ||
    null;

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: 'Report ID', value: <span className="font-mono text-[12.5px]">{detail.reportId}</span> },
    { label: 'Created', value: formatWhen(detail.createdAt) },
    { label: 'Requested by', value: requestedBy || '—' },
    { label: 'Base seed', value: detail.baseSeed },
    {
      label: 'Pipes',
      value: pipeIds == null ? 'All pipes' : `${pipeIds.length} selected`,
    },
    {
      label: 'How sure',
      value: confidence == null ? '—' : formatPct(confidence),
    },
    {
      label: 'How tight',
      value: margin == null ? '—' : formatPct(margin),
    },
    {
      label: 'Hi-lo threshold',
      value: hiLo != null ? `${hiLo} mm` : '—',
    },
    {
      label: 'Turning',
      value:
        flipping == null ? '—' : flipping ? 'allowed' : 'off',
    },
  ];

  return (
    <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden max-w-3xl">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-blueprint-blue" />
      <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-4">
        Original request
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {rows.map((r) => (
          <div key={r.label} className="border-b border-[#EEF0F3] pb-2">
            <dt className="text-[10px] font-bold tracking-wide uppercase text-heritage-grey">
              {r.label}
            </dt>
            <dd className="mt-0.5 text-[14px] text-blueprint-blue font-semibold">{r.value}</dd>
          </div>
        ))}
      </dl>
      {pipeIds && pipeIds.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-bold tracking-wide uppercase text-heritage-grey mb-2">
            Pipe IDs
          </p>
          <div className="max-h-[220px] overflow-auto rounded-[10px] border border-[#E2E5EA] bg-page-bg p-3 font-mono text-[12px] text-text-grey leading-relaxed">
            {pipeIds.join(', ')}
          </div>
        </div>
      )}
    </div>
  );
}

function optionalNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
